// ReturnWizard local state (useReducer) — wizard-scoped, NOT Redux.
//
// Thinking-in-React §3/§4: the minimal wizard state lives in the ReturnWizard
// parent because every step both renders from and writes to it. Derived values
// (selected total, item count, whether photos are required) are computed at the
// render site, never stored here.
import type {
  RefundMethod,
  ReturnReason,
  ShippingResponsibility,
} from "../../../../apis/return";

// One selected order line. `available` is line.availableReturnQuantity at load
// time; `quantity` is the buyer's chosen return qty (1..available).
export interface WizardSelectedItem {
  orderItemId: string;       // === order line.id
  selected: boolean;
  quantity: number;
  available: number;
  // line snapshot for the create payload + review (no re-fetch needed)
  productName: string;
  unitPrice: number;
  sku: string | null;
  image: string | null;
  unit: string | null;
  orderedQuantity: number;
  alreadyReturned: number;
  // per-item note (Step 1)
  note: string;
}

export interface WizardState {
  // Path B: the order chosen in Step 0 (null until picked). Path A/C: preset.
  orderId: string | null;
  // Step 1 — keyed by orderItemId for O(1) toggles.
  items: Record<string, WizardSelectedItem>;
  itemOrder: string[];        // stable render order
  // Step 2
  reason: ReturnReason | null;
  subReason: string | null;
  reasonDetails: string;
  // Step 3
  evidencePhotos: string[];   // uploaded media URLs (request-level)
  preferredRefundMethod: RefundMethod | null;     // D2 (collected; see report re: not sent)
  shippingResponsibility: ShippingResponsibility | null; // D11 (collected; see report)
  // Step 4
  consent: boolean;
}

export const initialWizardState = (orderId: string | null): WizardState => ({
  orderId,
  items: {},
  itemOrder: [],
  reason: null,
  subReason: null,
  reasonDetails: "",
  evidencePhotos: [],
  preferredRefundMethod: null,
  // D11 default — SELLER_PICKS_UP per tenant settings (screens-buyer-app §6).
  shippingResponsibility: "SELLER_PICKS_UP",
  consent: false,
});

export type WizardAction =
  | { type: "SET_ORDER"; orderId: string }
  | { type: "LOAD_ITEMS"; items: WizardSelectedItem[]; preselectedItemIds?: string[] }
  | { type: "TOGGLE_ITEM"; orderItemId: string; selected: boolean }
  | { type: "SET_ITEM_QTY"; orderItemId: string; quantity: number }
  | { type: "SET_ITEM_NOTE"; orderItemId: string; note: string }
  | { type: "SELECT_ALL"; selected: boolean }
  | { type: "SET_REASON"; reason: ReturnReason }
  | { type: "SET_SUB_REASON"; subReason: string | null }
  | { type: "SET_REASON_DETAILS"; reasonDetails: string }
  | { type: "SET_PHOTOS"; photos: string[] }
  | { type: "SET_REFUND_METHOD"; method: RefundMethod | null }
  | { type: "SET_SHIPPING"; shipping: ShippingResponsibility }
  | { type: "SET_CONSENT"; consent: boolean }
  | { type: "RESET_ITEMS" };

const clampQty = (q: number, available: number): number => {
  if (!Number.isFinite(q)) return 1;
  const floored = Math.floor(q);
  if (floored < 1) return 1;
  if (floored > available) return available;
  return floored;
};

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_ORDER":
      // New order picked → clear any stale item selection from a previous order.
      return { ...state, orderId: action.orderId, items: {}, itemOrder: [] };

    case "RESET_ITEMS":
      return { ...state, items: {}, itemOrder: [] };

    case "LOAD_ITEMS": {
      const preset = new Set(action.preselectedItemIds ?? []);
      const all = preset.size === 0 || preset.has("all");
      const items: Record<string, WizardSelectedItem> = {};
      const order: string[] = [];
      action.items.forEach((it) => {
        const selectable = it.available > 0;
        items[it.orderItemId] = {
          ...it,
          // Path A preselect: select the named line(s) (or all). Path B: nothing
          // preselected, buyer ticks manually. Fully-returned lines never select.
          selected: selectable && (all || preset.has(it.orderItemId)),
          quantity: it.available > 0 ? Math.min(it.quantity || 1, it.available) : 0,
        };
        order.push(it.orderItemId);
      });
      return { ...state, items, itemOrder: order };
    }

    case "TOGGLE_ITEM": {
      const cur = state.items[action.orderItemId];
      if (!cur || cur.available <= 0) return state;
      return {
        ...state,
        items: { ...state.items, [action.orderItemId]: { ...cur, selected: action.selected } },
      };
    }

    case "SET_ITEM_QTY": {
      const cur = state.items[action.orderItemId];
      if (!cur) return state;
      return {
        ...state,
        items: {
          ...state.items,
          [action.orderItemId]: { ...cur, quantity: clampQty(action.quantity, cur.available) },
        },
      };
    }

    case "SET_ITEM_NOTE": {
      const cur = state.items[action.orderItemId];
      if (!cur) return state;
      return {
        ...state,
        items: { ...state.items, [action.orderItemId]: { ...cur, note: action.note } },
      };
    }

    case "SELECT_ALL": {
      const items: Record<string, WizardSelectedItem> = {};
      state.itemOrder.forEach((id) => {
        const it = state.items[id];
        items[id] = it.available > 0 ? { ...it, selected: action.selected } : it;
      });
      return { ...state, items };
    }

    case "SET_REASON":
      // Changing reason invalidates the previously picked sub-reason.
      return { ...state, reason: action.reason, subReason: null };

    case "SET_SUB_REASON":
      return { ...state, subReason: action.subReason };

    case "SET_REASON_DETAILS":
      return { ...state, reasonDetails: action.reasonDetails };

    case "SET_PHOTOS":
      return { ...state, evidencePhotos: action.photos };

    case "SET_REFUND_METHOD":
      return { ...state, preferredRefundMethod: action.method };

    case "SET_SHIPPING":
      return { ...state, shippingResponsibility: action.shipping };

    case "SET_CONSENT":
      return { ...state, consent: action.consent };

    default:
      return state;
  }
}

// ── Derived selectors (computed, never stored) ──

export const selectedItems = (s: WizardState): WizardSelectedItem[] =>
  s.itemOrder.map((id) => s.items[id]).filter((it) => it && it.selected);

export const selectedTotal = (s: WizardState): number =>
  selectedItems(s).reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);

export const selectedCount = (s: WizardState): number => selectedItems(s).length;

// reason → free-text required (screens-buyer-app §5 validation)
export const REASON_REQUIRES_DETAIL: ReturnReason[] = ["OTHER", "PRICE_DISPUTE", "RECALL"];

export const reasonRequiresDetail = (reason: ReturnReason | null): boolean =>
  !!reason && REASON_REQUIRES_DETAIL.includes(reason);

// OTHER needs >= 10 chars; PRICE_DISPUTE / RECALL just need non-empty.
export const detailValid = (s: WizardState): boolean => {
  if (!s.reason) return false;
  if (!reasonRequiresDetail(s.reason)) return true;
  const trimmed = s.reasonDetails.trim();
  if (s.reason === "OTHER") return trimmed.length >= 10;
  return trimmed.length > 0;
};
